import { z } from 'zod';
import { parseJsonWithNativeSafeNumbers } from '../../lossless-json-codec';
import { JsonValueSchema } from '../../schemas/common.schemas';
import {
    CardPointeTokenizationResultType,
    type CardPointeCardSecureHostedIframeTokenizationResultV2,
    type CardPointeHostedIframeSuccessResponse,
} from '../../types/ipg/cardpointe/cardpointe-tokenization.types';

const CardPointeHostedIframeTokenSchema = z.string().regex(/^\d{16}$/);
const CardPointeHostedIframeExpirySchema = z.string().regex(/^\d{4}(?:[1-9]|1[0-2])$/);

const CardPointeHostedIframeBasicMessageSchema = z
    .object({
        message: CardPointeHostedIframeTokenSchema,
        expiry: CardPointeHostedIframeExpirySchema,
    })
    .catchall(JsonValueSchema);

const CardPointeHostedIframeEnhancedMessageSchema = z
    .object({
        token: CardPointeHostedIframeTokenSchema,
        errorCode: z.union([z.literal('0'), z.literal(0)]),
        errorMessage: z.string().optional(),
        expiry: CardPointeHostedIframeExpirySchema,
    })
    .catchall(JsonValueSchema);

const CardPointeHostedIframeValidationMessageSchema = z
    .object({
        validationError: z.union([z.literal(true), z.string().min(1)]),
        errorMessage: z.string().optional(),
    })
    .passthrough();

const CardPointeHostedIframePendingMessageSchema = z
    .object({
        message: z.literal(''),
    })
    .passthrough();

const CardPointeHostedIframeCssLoadedMessageSchema = z
    .object({
        cssLoaded: z.literal(true),
    })
    .passthrough();

export type CardPointeHostedIframeMessageResult =
    | {
          readonly outcome: 'tokenized';
          readonly token: string;
          readonly tokenizationResult: CardPointeCardSecureHostedIframeTokenizationResultV2;
      }
    | {
          readonly outcome: 'validation_error';
          readonly message: string;
      }
    | {
          readonly outcome: 'pending';
      }
    | {
          readonly outcome: 'css_loaded';
      };

export const CardPointeHostedIframeMessageContract = {
    variants: {
        'provider-unversioned-v1': {
            normalize: parseCardPointeHostedIframeMessage,
        },
    },
    writerVariant: 'provider-unversioned-v1',
} as const;

export function buildCardPointeHostedIframeUrl(
    cardSecureBaseUrl: string,
    hostedIframeCss?: string
): string {
    const url = new URL('/itoke/ajax-tokenizer.html', cardSecureBaseUrl);
    url.searchParams.set('useexpiry', 'true');
    url.searchParams.set('usecvv', 'true');
    url.searchParams.set('enhancedresponse', 'true');
    url.searchParams.set('invalidcreditcardevent', 'true');
    url.searchParams.set('invalidexpiryevent', 'true');
    url.searchParams.set('invalidcvvevent', 'true');
    url.searchParams.set('formatinput', 'true');
    url.searchParams.set('tokenizewheninactive', 'true');
    url.searchParams.set('inactivityto', '500');
    if (hostedIframeCss !== undefined) {
        url.searchParams.set('orientation', 'custom');
        url.searchParams.set('css', hostedIframeCss);
        url.searchParams.set('sendcssloadedevent', 'true');
    }
    return url.toString();
}

export function resolveCardPointeHostedIframeToken(
    response: CardPointeHostedIframeSuccessResponse
): string {
    const enhanced = CardPointeHostedIframeEnhancedMessageSchema.safeParse(response);
    return enhanced.success
        ? enhanced.data.token
        : CardPointeHostedIframeBasicMessageSchema.parse(response).message;
}

export function resolveCardPointeHostedIframeExpiryDate(
    response: CardPointeHostedIframeSuccessResponse
): string {
    const expiry = CardPointeHostedIframeExpirySchema.parse(response.expiry);
    return `${expiry.slice(4).padStart(2, '0')}/${expiry.slice(2, 4)}`;
}

export function parseCardPointeHostedIframeMessage(
    data: unknown
): CardPointeHostedIframeMessageResult {
    const serializedMessage = z.string().safeParse(data);
    if (!serializedMessage.success) {
        throw new Error('CardPointe hosted card entry returned an invalid message.');
    }

    let parsed: unknown;
    try {
        parsed = parseJsonWithNativeSafeNumbers(serializedMessage.data);
    } catch {
        throw new Error('CardPointe hosted card entry returned an invalid message.');
    }

    if (CardPointeHostedIframeCssLoadedMessageSchema.safeParse(parsed).success) {
        return { outcome: 'css_loaded' };
    }

    const enhanced = CardPointeHostedIframeEnhancedMessageSchema.safeParse(parsed);
    if (enhanced.success) {
        return {
            outcome: 'tokenized',
            token: enhanced.data.token,
            tokenizationResult: {
                result_type:
                    CardPointeTokenizationResultType.CARDSECURE_HOSTED_IFRAME_TOKENIZATION_V2,
                provider_response: enhanced.data,
                token: enhanced.data.token,
            },
        };
    }

    const basic = CardPointeHostedIframeBasicMessageSchema.safeParse(parsed);
    if (basic.success) {
        return {
            outcome: 'tokenized',
            token: basic.data.message,
            tokenizationResult: {
                result_type:
                    CardPointeTokenizationResultType.CARDSECURE_HOSTED_IFRAME_TOKENIZATION_V2,
                provider_response: basic.data,
                token: basic.data.message,
            },
        };
    }

    const validation = CardPointeHostedIframeValidationMessageSchema.safeParse(parsed);
    if (validation.success) {
        const validationErrorMessage = z.string().safeParse(validation.data.validationError);
        const validationMessage = validationErrorMessage.success
            ? validationErrorMessage.data.trim()
            : '';
        return {
            outcome: 'validation_error',
            message:
                validation.data.errorMessage?.trim() ||
                validationMessage ||
                'Check the card number, expiration date, and CVV.',
        };
    }

    if (CardPointeHostedIframePendingMessageSchema.safeParse(parsed).success) {
        return { outcome: 'pending' };
    }

    throw new Error('CardPointe hosted card entry returned an invalid message.');
}
